import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { filter, take } from 'rxjs';
import { IMediaLockable } from 'src/app/api/modules/core/dynamic/components/IMediaLock';
import { MediaComponent } from '../../../dynamic/components/media/media.component';
import { MediaLockableRepository } from './media-lockable.repository';

/**
 * The Media Lockable Component is used to Force a user to watch Media.
 *
 * This component is not intended to be used directly, but by the {@link MediaLockComponent}
 * and the {@link DialogMediaLockComponent}.
 */
@Component({
  selector: 'app-media-lockable',
  templateUrl: './media-lockable.component.html',
  styleUrls: ['./media-lockable.component.scss'],
  providers: [MediaLockableRepository],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MediaComponent, MatProgressBar],
})
export class MediaLockableComponent implements OnInit {
  protected readonly mediaLockableRepository = inject(MediaLockableRepository);
  private readonly destroyRef = inject(DestroyRef);

  // emit-once bridges over the repository signals, created eagerly here (in the
  // injection context) so ngOnInit can subscribe to them
  private readonly unlockedOnce$ = toObservable(this.mediaLockableRepository.unlocked).pipe(
    filter((unlocked) => unlocked),
    take(1),
  );
  private readonly errorOnce$ = toObservable(this.mediaLockableRepository.error).pipe(
    filter((error) => error),
    take(1),
  );

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // CONFIG
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * The Configuration. Applied once when the component initializes.
   */
  readonly config = input.required<IMediaLockable>();

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // OUTPUTS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Emits once when the media lock is unlocked.
   */
  readonly unlocked = output<void>();

  /**
   * Emits once when the Media has experienced an error.
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly error = output<void>();

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // LIFECYCLE
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Lifecycle Hook
   */
  ngOnInit() {
    // configure the system
    this.mediaLockableRepository.applyConfiguration(this.config());

    // report an error once
    this.errorOnce$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.error.emit();
    });

    // report the unlock once
    this.unlockedOnce$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.unlocked.emit();
    });
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  // MEDIA EVENTS
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Occurs when the Media is Playing.
   */
  onMediaPlaying() {
    this.mediaLockableRepository.applyInitialize();
  }

  /**
   * Occurs when the Media time update occurs.
   */
  onMediaCurrentTime(currentTime = 0) {
    this.mediaLockableRepository.applyCurrentTime(currentTime);
  }

  /**
   * Occurs when the Media Duration is known.
   */
  onMediaDuration(duration = 0) {
    this.mediaLockableRepository.applyDuration(duration);
  }

  /**
   * Occurs when the Media throws an error.
   */
  onMediaError() {
    this.mediaLockableRepository.applyError();
  }

  /**
   * Occurs if the media is in portrait mode or not.
   *
   * @param isPortrait - whether the media is in portrait mode.
   */
  onMediaIsPortrait(isPortrait: boolean) {
    this.mediaLockableRepository.applyMediaIsPortrait(isPortrait);
  }
}
